import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography
} from "@material-ui/core";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import ImageIcon from "@material-ui/icons/Image";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import api from "../../services/api";
import officialCampaignService from "../../services/officialCampaignService";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    background: "#f3f6fb"
  },
  dialogTitle: {
    padding: theme.spacing(2.5, 3),
    borderBottom: "1px solid #dbe4ee",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)"
  },
  dialogTitleRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column"
    }
  },
  dialogEyebrow: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#3566d6",
    marginBottom: theme.spacing(0.5)
  },
  dialogSubtitle: {
    color: theme.palette.text.secondary,
    maxWidth: 760,
    lineHeight: 1.5
  },
  dialogContent: {
    padding: theme.spacing(3),
    background: "#f3f6fb"
  },
  workspaceLayout: {
    alignItems: "flex-start"
  },
  editorPane: {
    padding: theme.spacing(3),
    borderRadius: 18,
    border: "1px solid #d9e2ec",
    background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
    boxShadow: "0 12px 28px rgba(20, 48, 75, 0.06)"
  },
  editorPaneHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  editorPaneTitle: {
    fontWeight: 700,
    color: "#14304b"
  },
  editorPaneSubtitle: {
    color: theme.palette.text.secondary,
    lineHeight: 1.5
  },
  editorStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
  },
  workspaceColumn: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
  },
  stickyColumn: {
    position: "sticky",
    top: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
      position: "static"
    }
  },
  templateEditor: {
    fontFamily: "monospace",
    minHeight: 200
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  sectionCard: {
    padding: theme.spacing(2),
    borderRadius: 10,
    background: "#fff"
  },
  sectionHint: {
    color: theme.palette.text.secondary,
    lineHeight: 1.5
  },
  offlineDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: theme.spacing(1)
  },
  previewWorkspace: {
    padding: theme.spacing(2.5),
    borderRadius: 18,
    border: "1px solid #dfe5eb",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
    boxShadow: "0 18px 34px rgba(20, 48, 75, 0.08)"
  },
  previewPane: {
    position: "sticky",
    top: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
      position: "static"
    }
  },
  previewCard: {
    padding: theme.spacing(2),
    borderRadius: 12,
    border: "1px solid #d7dee7",
    background: "#ffffff",
    height: "100%"
  },
  previewFieldLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  previewHint: {
    color: theme.palette.text.secondary,
    lineHeight: 1.5
  },
  previewSummary: {
    marginTop: theme.spacing(1.5)
  },
  actionPanel: {
    padding: theme.spacing(2),
    borderRadius: 14,
    border: "1px solid #dbe4ee",
    background: "linear-gradient(180deg, #ffffff 0%, #eef5ff 100%)"
  },
  actionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },
  actionButton: {
    minHeight: 44,
    fontWeight: 700
  },
  actionButtonWide: {
    gridColumn: "1 / -1"
  },
  metricsCard: {
    padding: theme.spacing(2),
    borderRadius: 14,
    border: "1px solid #dbe4ee",
    background: "#ffffff"
  },
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },
  metricItem: {
    padding: theme.spacing(1.5),
    borderRadius: 12,
    background: "#f7faff",
    border: "1px solid #e2ebf5"
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: theme.spacing(0.5)
  },
  metricValue: {
    fontSize: 15,
    fontWeight: 700,
    color: "#14304b"
  },
  helperRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  scheduleQuickActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1)
  },
  previewDataCard: {
    padding: theme.spacing(2),
    borderRadius: 14,
    border: "1px solid #dbe4ee",
    background: "#ffffff"
  },
  previewDataCardDense: {
    padding: theme.spacing(2),
    borderRadius: 14,
    border: "1px solid #dbe4ee",
    background: "#ffffff"
  },
  phoneContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start"
  },
  phoneFrame: {
    width: 290,
    minHeight: 590,
    backgroundColor: "#1a1a1a",
    borderRadius: 36,
    padding: 8,
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.45)",
    position: "relative"
  },
  phoneNotch: {
    position: "absolute",
    top: 8,
    left: "50%",
    transform: "translateX(-50%)",
    width: 84,
    height: 24,
    backgroundColor: "#1a1a1a",
    borderRadius: 12,
    zIndex: 10
  },
  phoneScreen: {
    width: "100%",
    minHeight: 574,
    backgroundColor: "#0b141a",
    borderRadius: 28,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column"
  },
  phoneHeader: {
    backgroundColor: "#1f2c34",
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    gap: 12
  },
  phoneAvatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: "#00a884",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: 700,
    fontSize: 16
  },
  phoneHeaderInfo: {
    flex: 1
  },
  phoneHeaderName: {
    color: "#e9edef",
    fontSize: 14,
    fontWeight: 600
  },
  phoneHeaderStatus: {
    color: "#8696a0",
    fontSize: 12
  },
  phoneChat: {
    flex: 1,
    padding: 16,
    overflowY: "auto",
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23182229' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    backgroundColor: "#0b141a"
  },
  phoneBubble: {
    backgroundColor: "#005c4b",
    borderRadius: "8px 8px 0 8px",
    padding: "8px 12px",
    maxWidth: "88%",
    marginLeft: "auto",
    marginBottom: 8
  },
  phoneBubbleSecondary: {
    backgroundColor: "#182229"
  },
  phoneMessageText: {
    color: "#e9edef",
    fontSize: 13,
    lineHeight: 1.45,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word"
  },
  phoneMessageHeader: {
    color: "#ffffff",
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 6,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word"
  },
  phoneMessageFooter: {
    color: "#aebac1",
    fontSize: 11,
    marginTop: 6,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word"
  },
  phoneMessageTime: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 4,
    marginTop: 4
  },
  phoneMessageTimeText: {
    color: "#8696a0",
    fontSize: 11
  },
  phoneMessageCheck: {
    color: "#53bdeb",
    fontSize: 16
  },
  previewImageFrame: {
    width: "100%",
    minHeight: 150,
    maxHeight: 190,
    overflow: "hidden",
    borderRadius: 8,
    background: "#1f2c34",
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  previewImage: {
    display: "block",
    width: "100%",
    height: "auto"
  },
  previewImagePlaceholder: {
    width: "100%",
    minHeight: 150,
    color: "#aebac1",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: 16,
    textAlign: "center"
  },
  previewActionWrap: {
    marginTop: 10,
    display: "flex",
    flexWrap: "wrap",
    gap: 6
  },
  previewActionChip: {
    backgroundColor: "rgba(255,255,255,0.08)",
    color: "#d9fdd3",
    border: "1px solid rgba(217,253,211,0.18)"
  },
  templateButtonsWrap: {
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 10,
    backgroundColor: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.08)"
  },
  templateButtonsLabel: {
    color: "#aebac1",
    fontSize: 11,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    marginBottom: 8
  },
  templateButtonsList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8
  },
  templateButtonChip: {
    backgroundColor: "rgba(255,255,255,0.12)",
    color: "#e9edef",
    border: "1px solid rgba(255,255,255,0.18)"
  },
  templateButtonDangerChip: {
    backgroundColor: "rgba(244, 67, 54, 0.16)",
    color: "#ffb3ae",
    border: "1px solid rgba(244, 67, 54, 0.32)"
  },
  previewListCard: {
    marginTop: 10,
    padding: 10,
    borderRadius: 8,
    backgroundColor: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.08)"
  },
  previewListButton: {
    marginTop: 8,
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: 8,
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "#e9edef",
    fontSize: 12,
    fontWeight: 600
  },
  emptyPreviewState: {
    color: "#8696a0",
    textAlign: "center",
    padding: theme.spacing(5, 2),
    fontSize: 13,
    lineHeight: 1.5
  },
  dialogActions: {
    padding: theme.spacing(2, 3, 3),
    borderTop: "1px solid #dbe4ee",
    background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)"
  },
  footerActionsWrap: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch"
    }
  },
  footerHint: {
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    maxWidth: 520
  },
  footerButtons: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    justifyContent: "flex-end"
  }
}));

const defaultForm = {
  name: "",
  officialContactListId: "",
  whatsappId: "",
  safeMode: true,
  requireOptIn: false,
  dailyLimit: 200,
  hourlyLimit: 50,
  stopOnErrorRate: 20,
  stopOnOptOutRate: 5,
  ticketRegister: false,
  ticketStatus: "closed",
  ticketQueueId: "",
  ticketUserId: "",
  scheduledAt: "",
  templateName: "",
  templateLanguage: "",
  templateComponentsJson: "[]",
  templateValues: {},
  headerMediaUrl: "",
  sendInteractive: false,
  interactiveType: "button",
  interactiveBodyText: "",
  interactiveButtonText: "Escolha uma opção",
  interactiveButtons: [{ id: "1", title: "" }, { id: "2", title: "" }, { id: "3", title: "" }],
  interactiveSections: [{ title: "Opções", rows: [{ id: "1", title: "" }] }],
  templateMoreInfoText: ""
};

const defaultPreviewContact = {
  name: "",
  number: "5511999999999",
  var1: "",
  var2: "",
  var3: "",
  var4: "",
  var5: "",
  var6: ""
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }
  return [];
};

const asPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const normalizeCollection = (value, keys = ["records", "data", "users", "whatsapps", "queues", "items"]) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string") {
    try {
      return normalizeCollection(JSON.parse(value), keys);
    } catch (_) {
      return [];
    }
  }
  if (!value || typeof value !== "object") return [];

  for (const key of keys) {
    if (Array.isArray(value[key])) {
      return value[key].filter(Boolean);
    }
  }

  return [];
};

const sameId = (left, right) => String(left) === String(right);

const asText = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return fallback;
};

const getFirstName = (name) => String(name || "").trim().split(" ").filter(Boolean)[0] || "";

const buildPreviewContactFromListItem = (item = {}) => ({
  name: asText(item.name),
  number: asText(item.number, defaultPreviewContact.number),
  var1: asText(item.var1),
  var2: asText(item.var2),
  var3: asText(item.var3),
  var4: asText(item.var4),
  var5: asText(item.var5),
  var6: asText(item.var6)
});

const escapeRegExp = (value) => String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const replaceTemplatePlaceholders = (value, context) => {
  if (!value) return "";
  const now = new Date();
  const currentDate = now.toLocaleDateString("pt-BR");
  const currentHour = now.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit"
  });

  const mappings = [
    { key: "{{contact.name}}", val: context.name || "" },
    { key: "{{contact.firstName}}", val: context.firstName || "" },
    { key: "{{contact.number}}", val: context.number || "" },
    { key: "{{contact.var1}}", val: context.var1 || "" },
    { key: "{{contact.var2}}", val: context.var2 || "" },
    { key: "{{contact.var3}}", val: context.var3 || "" },
    { key: "{{contact.var4}}", val: context.var4 || "" },
    { key: "{{contact.var5}}", val: context.var5 || "" },
    { key: "{{contact.var6}}", val: context.var6 || "" },
    { key: "{{name}}", val: context.name || "" },
    { key: "{{firstName}}", val: context.firstName || "" },
    { key: "{{number}}", val: context.number || "" },
    { key: "{{var1}}", val: context.var1 || "" },
    { key: "{{var2}}", val: context.var2 || "" },
    { key: "{{var3}}", val: context.var3 || "" },
    { key: "{{var4}}", val: context.var4 || "" },
    { key: "{{var5}}", val: context.var5 || "" },
    { key: "{{var6}}", val: context.var6 || "" },
    { key: "{{1}}", val: context.var1 || "" },
    { key: "{{2}}", val: context.var2 || "" },
    { key: "{{3}}", val: context.var3 || "" },
    { key: "{{4}}", val: context.var4 || "" },
    { key: "{{5}}", val: context.var5 || "" },
    { key: "{{6}}", val: context.var6 || "" },
    { key: "{{date}}", val: currentDate },
    { key: "{{hour}}", val: currentHour },
    { key: "{{data_hora}}", val: `${currentDate} às ${currentHour}` }
  ];

  return mappings.reduce((result, mapping) => (
    result.replace(new RegExp(escapeRegExp(mapping.key), "gi"), mapping.val)
  ), String(value));
};

const getTemplateKey = (template) =>
  [template?.id, template?.name, template?.language].filter(Boolean).join("__");

const getTemplateComponent = (template, type) =>
  asArray(template?.components).find(component => String(component.type).toUpperCase() === type) || null;

const getTemplateBody = (template) =>
  asText(getTemplateComponent(template, "BODY")?.text);

const getTemplateFooter = (template) =>
  asText(getTemplateComponent(template, "FOOTER")?.text);

const getTemplateHeaderComponent = (template) => getTemplateComponent(template, "HEADER");

const getTemplateButtons = (template) => {
  const buttonsComponent = getTemplateComponent(template, "BUTTONS");
  const buttons = asArray(buttonsComponent?.buttons);
  return buttons
    .map((button, index) => ({
      id: asText(button?.id, String(index + 1)),
      title: asText(button?.text || button?.title, `Botão ${index + 1}`),
      type: String(button?.type || "quick_reply").toLowerCase()
    }))
    .filter(button => button.title.trim());
};

const getTemplateVariables = (template) => {
  const matches = getTemplateBody(template).match(/\{\{(\w+)\}\}/g) || [];
  return [...new Set(matches.map(match => match.replace(/\{\{|\}\}/g, "")))];
};

const hasImageHeader = (template) =>
  asArray(template?.components).some(component =>
    String(component.type).toUpperCase() === "HEADER" &&
    String(component.format).toUpperCase() === "IMAGE"
  );

const getVariableLabel = (list, key) =>
  asText(list?.variableLabels?.[key], `Variável ${String(key).replace("var", "")}`);

const buildPreviewMissingValue = (label) => `[sem valor em ${label}]`;

const createDefaultTemplateValues = (template) =>
  getTemplateVariables(template).reduce((values, variable, index) => {
    values[`var${index + 1}`] = index === 0
      ? { source: "firstName", column: "", value: "" }
      : { source: "fixed", column: "", value: "" };
    return values;
  }, {});

const getTemplateValueConfig = (templateValues, key, index) => ({
  source: index === 0 ? "firstName" : "fixed",
  column: "",
  value: "",
  ...asPlainObject(asPlainObject(templateValues)?.[key])
});

const normalizeTemplateValues = (templateValues, variables) =>
  variables.reduce((values, _variable, index) => {
    const key = `var${index + 1}`;
    values[key] = getTemplateValueConfig(templateValues, key, index);
    return values;
  }, {});

const toDateTimeLocalValue = (date) => {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
};

const getDefaultScheduledAt = (minutesAhead = 5) => {
  const date = new Date(Date.now() + minutesAhead * 60 * 1000);
  date.setSeconds(0, 0);
  return toDateTimeLocalValue(date);
};

const toSchedulePayloadValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const OfficialCampaignModal = ({ open, onClose, onSuccess, initialOfficialContactListId, campaignId = null }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const history = useHistory();
  const isEditMode = Boolean(campaignId);

  const [form, setForm] = useState(defaultForm);
  const [officialContactLists, setOfficialContactLists] = useState([]);
  const [contactLists] = useState([]);
  const [tags] = useState([]);
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [syncingTemplates, setSyncingTemplates] = useState(false);
  const [uploadingHeaderImage, setUploadingHeaderImage] = useState(false);
  const [previewContact, setPreviewContact] = useState(defaultPreviewContact);

  const companyId = user?.companyId;

  useEffect(() => {
    if (!open || !companyId) {
      // Reset form quando modal fechar
      if (!open) {
        setForm(defaultForm);
        setSelectedTemplate(null);
        setTemplates([]);
        setPreviewContact(defaultPreviewContact);
        setLoadingRecord(false);
      }
      return;
    }

    const fetchData = async () => {
      const fallbackUsers = user?.id ? [{
        id: user.id,
        name: user.name || "Usuário atual"
      }] : [];

      setOfficialContactLists([]);
      setQueues([]);
      setUsers(user?.profile === "admin" ? [] : fallbackUsers);
      setWhatsapps([]);

      try {
        const requests = [
          {
            key: "metaLists",
            required: true,
            request: api.get("/official-contact-lists", { params: { pageSize: 100 } })
          },
          {
            key: "queues",
            required: false,
            request: api.get("/queue")
          },
          {
            key: "users",
            required: false,
            request: user?.profile === "admin"
              ? api.get("/users/", { params: { showAll: true } })
              : Promise.resolve({ data: fallbackUsers })
          },
          {
            key: "whatsapps",
            required: true,
            request: api.get("/whatsapp", { params: { session: 0 } })
          }
        ];

        const results = await Promise.allSettled(requests.map(item => item.request));

        results.forEach((result, index) => {
          const { key, required } = requests[index];

          if (result.status !== "fulfilled") {
            console.error(`[OfficialCampaignModal] Falha ao carregar ${key}`, result.reason);
            if (required) {
              toastError(result.reason);
            }
            return;
          }

          const payload = result.value?.data;

          if (key === "metaLists") {
            setOfficialContactLists(normalizeCollection(payload).filter(list => list?.id));
            return;
          }

          if (key === "queues") {
            setQueues(normalizeCollection(payload).filter(queue => queue?.id));
            return;
          }

          if (key === "users") {
            setUsers(normalizeCollection(payload, ["users", "records", "data"]).filter(item => item?.id));
            return;
          }

          if (key === "whatsapps") {
            const officials = normalizeCollection(payload, ["whatsapps", "records", "data"]).filter(
              w => w?.id && w.channel === "whatsapp_oficial"
            );
            setWhatsapps(officials);
          }
        });
      } catch (err) {
        toastError(err);
      }
    };

    fetchData();
  }, [open, companyId, user]);

  useEffect(() => {
    if (open && initialOfficialContactListId && !isEditMode) {
      setForm((prev) => ({ ...prev, officialContactListId: initialOfficialContactListId }));
    }
  }, [open, initialOfficialContactListId, isEditMode]);

  useEffect(() => {
    if (open && !isEditMode) {
      setForm((prev) => prev.scheduledAt ? prev : {
        ...prev,
        scheduledAt: getDefaultScheduledAt()
      });
    }
  }, [open, isEditMode]);

  const handleChange = useCallback((field) => (event) => {
    const target = event?.target || {};
    const value = target.value ?? "";
    if (["ticketRegister", "safeMode", "requireOptIn"].includes(field)) {
      setForm(prev => ({ ...prev, [field]: Boolean(target.checked) }));
    } else {
      setForm(prev => ({ ...prev, [field]: value }));
    }
  }, []);

  const handleSelectTemplate = useCallback((template) => {
    const components = asArray(template?.components);
    setSelectedTemplate(template);
    setForm(prev => ({
      ...prev,
      templateName: template?.name || "",
      templateLanguage: template?.language || "",
      templateComponentsJson: JSON.stringify(components, null, 2),
      templateValues: createDefaultTemplateValues(template),
      headerMediaUrl: ""
    }));
  }, []);

  const handleLoadTemplates = useCallback(async (whatsappId) => {
    if (!whatsappId) {
      setTemplates([]);
      return;
    }
    setLoadingTemplates(true);
    try {
      const { data } = await officialCampaignService.templates(whatsappId);
      setTemplates(
        normalizeCollection(data?.data || data)
          .filter(template => template?.name && template?.language)
      );
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  const handleSyncTemplates = useCallback(async () => {
    if (!form.whatsappId) return;
    setSyncingTemplates(true);
    try {
      await officialCampaignService.syncTemplates(form.whatsappId);
      await handleLoadTemplates(form.whatsappId);
      toast.success("Modelos aprovados sincronizados com a Meta.");
    } catch (err) {
      toastError(err);
    } finally {
      setSyncingTemplates(false);
    }
  }, [form.whatsappId, handleLoadTemplates]);

  useEffect(() => {
    if (!open || !campaignId) return;

    const hydrateCampaign = async () => {
      setLoadingRecord(true);
      try {
        const { data } = await officialCampaignService.show(campaignId);
        const templateComponents = asArray(data?.templateComponents);
        const rawTemplateValues = asPlainObject(data?.templateValues);
        const interactivePayload = asPlainObject(data?.interactivePayload);
        const { headerMediaUrl = "", ...storedTemplateValues } = rawTemplateValues;
        const template = {
          id: data?.id,
          name: data?.templateName || "",
          language: data?.templateLanguage || "",
          category: data?.templateCategory || "",
          components: templateComponents
        };

        setSelectedTemplate(template);
        setForm({
          ...defaultForm,
          name: asText(data?.name),
          officialContactListId: data?.officialContactListId || "",
          whatsappId: data?.whatsappId || "",
          safeMode: data?.safeMode !== false,
          requireOptIn: data?.requireOptIn === true,
          dailyLimit: data?.dailyLimit || 200,
          hourlyLimit: data?.hourlyLimit || 50,
          stopOnErrorRate: Math.round((Number(data?.stopOnErrorRate) || 0.2) * 100),
          stopOnOptOutRate: Math.round((Number(data?.stopOnOptOutRate) || 0.05) * 100),
          ticketRegister: Boolean(data?.ticketRegister),
          ticketStatus: data?.ticketStatus || "closed",
          ticketQueueId: data?.ticketQueueId || "",
          ticketUserId: data?.ticketUserId || "",
          scheduledAt: data?.scheduledAt ? toDateTimeLocalValue(new Date(data.scheduledAt)) : getDefaultScheduledAt(),
          templateName: data?.templateName || "",
          templateLanguage: data?.templateLanguage || "",
          templateComponentsJson: JSON.stringify(templateComponents, null, 2),
          templateValues: Object.keys(storedTemplateValues).length ? storedTemplateValues : createDefaultTemplateValues(template),
          headerMediaUrl: asText(headerMediaUrl),
          sendInteractive: Boolean(interactivePayload?.type && interactivePayload?.bodyText),
          interactiveType: interactivePayload?.type === "list" ? "list" : "button",
          interactiveBodyText: asText(interactivePayload?.bodyText),
          interactiveButtonText: asText(interactivePayload?.buttonText, defaultForm.interactiveButtonText),
          interactiveButtons: interactivePayload?.type === "button" && Array.isArray(interactivePayload?.buttons)
            ? defaultForm.interactiveButtons.map((button, index) => ({
                id: String(index + 1),
                title: asText(interactivePayload.buttons?.[index]?.title)
              }))
            : defaultForm.interactiveButtons,
          interactiveSections: interactivePayload?.type === "list" && Array.isArray(interactivePayload?.sections)
            ? [{
                title: asText(interactivePayload.sections?.[0]?.title, "Opções"),
                rows: Array.isArray(interactivePayload.sections?.[0]?.rows) && interactivePayload.sections[0].rows.length
                  ? interactivePayload.sections[0].rows.map((row, index) => ({
                      id: asText(row?.id, String(index + 1)),
                      title: asText(row?.title)
                    }))
                  : [{ id: "1", title: "" }]
              }]
            : defaultForm.interactiveSections,
          templateMoreInfoText: asText(interactivePayload?.templateButtonActions?.moreInfoText || interactivePayload?.templateMoreInfoText)
        });

        setPreviewContact(prev => ({
          ...prev,
          name: prev.name || defaultPreviewContact.name,
          number: prev.number || defaultPreviewContact.number
        }));

        if (data?.whatsappId) {
          await handleLoadTemplates(data.whatsappId);
        }
      } catch (err) {
        toastError(err);
        onClose?.();
      } finally {
        setLoadingRecord(false);
      }
    };

    hydrateCampaign();
  }, [open, campaignId, handleLoadTemplates, onClose]);

  const handleHeaderImageUpload = useCallback(async (event) => {
    const file = event?.target?.files?.[0];
    if (event?.target) {
      event.target.value = "";
    }

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Selecione uma imagem em PNG, JPG ou JPEG.");
      return;
    }

    const formData = new FormData();
    formData.append("image", file);

    setUploadingHeaderImage(true);
    try {
      const { data } = await officialCampaignService.uploadHeaderImage(formData);
      setForm(prev => ({ ...prev, headerMediaUrl: data.url || "" }));
      toast.success("Imagem enviada e pronta para a campanha.");
    } catch (err) {
      toastError(err);
    } finally {
      setUploadingHeaderImage(false);
    }
  }, []);

  const templateVariables = useMemo(() => getTemplateVariables(selectedTemplate), [selectedTemplate]);
  const templateHasImage = useMemo(() => hasImageHeader(selectedTemplate), [selectedTemplate]);

  const contactListOptions = useMemo(() => officialContactLists.map((list) => ({
    ...list,
    value: `meta_${list.id}`,
    label: `${asText(list.name, "Sem nome")}${list.contactsCount != null ? ` (${list.contactsCount})` : ""}`
  })), [officialContactLists]);

  const selectedContactListOption = useMemo(() => {
    return contactListOptions.find(option => sameId(option.id, form.officialContactListId)) || null;
  }, [form.officialContactListId, contactListOptions]);

  const selectedOfficialContactList = useMemo(
    () => officialContactLists.find(list => sameId(list.id, form.officialContactListId)) || null,
    [officialContactLists, form.officialContactListId]
  );

  useEffect(() => {
    if (!open) return;

    if (!selectedOfficialContactList?.id) {
      setPreviewContact(defaultPreviewContact);
      return;
    }

    let isMounted = true;

    const loadPreviewContact = async () => {
      try {
        const { data } = await api.get(`/official-contact-lists/${selectedOfficialContactList.id}/items`, {
          params: {
            pageNumber: 1,
            pageSize: 1
          }
        });

        if (!isMounted) return;

        const sampleItem = normalizeCollection(data, ["records", "items", "data"])[0];
        if (sampleItem) {
          setPreviewContact(buildPreviewContactFromListItem(sampleItem));
          return;
        }

        setPreviewContact(defaultPreviewContact);
      } catch (err) {
        if (!isMounted) return;
        setPreviewContact(defaultPreviewContact);
      }
    };

    loadPreviewContact();

    return () => {
      isMounted = false;
    };
  }, [open, selectedOfficialContactList]);

  const previewBaseContext = useMemo(() => ({
    name: asText(previewContact.name),
    firstName: getFirstName(previewContact.name),
    number: asText(previewContact.number),
    var1: asText(previewContact.var1),
    var2: asText(previewContact.var2),
    var3: asText(previewContact.var3),
    var4: asText(previewContact.var4),
    var5: asText(previewContact.var5),
    var6: asText(previewContact.var6)
  }), [previewContact]);

  const previewContext = useMemo(() => {
    const configuredValues = asPlainObject(form.templateValues);
    const nextContext = {
      ...previewBaseContext,
      headerMediaUrl: asText(form.headerMediaUrl)
    };

    for (let index = 1; index <= 6; index++) {
      const key = `var${index}`;
      const config = configuredValues[key];

      if (!config?.source) continue;

      if (config.source === "fixed") {
        nextContext[key] = String(config.value || "").trim() || `[texto fixo vazio em ${key}]`;
        continue;
      }

      if (config.source === "name") {
        nextContext[key] = nextContext.name || `[sem nome completo em ${key}]`;
        continue;
      }

      if (config.source === "firstName") {
        nextContext[key] = nextContext.firstName || `[sem primeiro nome em ${key}]`;
        continue;
      }

      if (config.source === "listVar") {
        const column = config.column || key;
        const label = getVariableLabel(selectedOfficialContactList, column);
        nextContext[key] = String(previewBaseContext[column] || "").trim() || buildPreviewMissingValue(label);
      }
    }

    return nextContext;
  }, [form.templateValues, form.headerMediaUrl, previewBaseContext, selectedOfficialContactList]);

  const templateHeader = useMemo(() => getTemplateHeaderComponent(selectedTemplate), [selectedTemplate]);
  const templateButtonsPreview = useMemo(
    () => getTemplateButtons(selectedTemplate),
    [selectedTemplate]
  );
  const templateHasMoreInfoButton = useMemo(
    () => templateButtonsPreview.some(button => /mais informações/i.test(button.title)),
    [templateButtonsPreview]
  );
  const templateBodyPreview = useMemo(
    () => replaceTemplatePlaceholders(getTemplateBody(selectedTemplate), previewContext),
    [selectedTemplate, previewContext]
  );
  const templateFooterPreview = useMemo(
    () => replaceTemplatePlaceholders(getTemplateFooter(selectedTemplate), previewContext),
    [selectedTemplate, previewContext]
  );
  const templateHeaderTextPreview = useMemo(() => {
    if (!templateHeader) return "";
    if (String(templateHeader.format).toUpperCase() !== "TEXT") return "";
    return replaceTemplatePlaceholders(asText(templateHeader.text), previewContext);
  }, [templateHeader, previewContext]);
  const templateHeaderImageUrl = useMemo(() => {
    if (!templateHasImage) return "";
    return asText(form.headerMediaUrl);
  }, [templateHasImage, form.headerMediaUrl]);
  const previewTime = useMemo(() => new Date().toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit"
  }), []);

  const estimateContacts = useMemo(() => {
    if (form.officialContactListId) {
      const selected = officialContactLists.find(list => sameId(list.id, form.officialContactListId));
      if (selected?.contactsCount !== undefined) return selected.contactsCount;
      if (selected?.totalImported !== undefined) return selected.totalImported;
      return null;
    }

    if (form.contactListId) {
      const selected = contactLists.find(list => sameId(list.id, form.contactListId));
      // Tentar diferentes campos que podem conter o count
      if (selected?.contactsCount !== undefined) return selected.contactsCount;
      if (selected?.total !== undefined) return selected.total;
      if (Array.isArray(selected?.contacts)) return selected.contacts.length;
      // Se não tiver, retornar null para indicar que precisa buscar
      return null;
    }

    if (form.tagIds?.length) {
      const total = form.tagIds.reduce((acc, tagId) => {
        const tag = tags.find(item => sameId(item.id, tagId));
        if (!tag) return acc;
        if (Array.isArray(tag.contacts)) {
          return acc + tag.contacts.length;
        }
        if (tag.count !== undefined) return acc + tag.count;
        return acc;
      }, 0);
      return total > 0 ? total : null;
    }

    return null;
  }, [form.contactListId, form.officialContactListId, form.tagIds, contactLists, officialContactLists, tags]);

  const estimatedDuration = useMemo(() => {
    if (!estimateContacts || estimateContacts === null) return "—";
    // Usar intervalo padrão de 20 segundos (mesmo das configurações de campanha)
    const intervalSeconds = 20;
    const totalSeconds = estimateContacts * intervalSeconds;
    const minutes = Math.ceil(totalSeconds / 60);
    if (minutes < 1) return "< 1 min";
    return `${minutes} min`;
  }, [estimateContacts]);

  const connectionStatus = useMemo(() => {
    const conn = whatsapps.find(item => sameId(item.id, form.whatsappId));
    if (!conn) return null;
    return conn.status;
  }, [whatsapps, form.whatsappId]);

  const resetForm = () => {
    setForm({ ...defaultForm });
    setSelectedTemplate(null);
    setTemplates([]);
    setPreviewContact(defaultPreviewContact);
  };

  const handleSubmit = async (mode = "draft") => {
    try {
      if (!form.whatsappId || !selectedTemplate) {
        toast.error("Selecione uma conexão e um template.");
        return;
      }

      if (!form.officialContactListId) {
        toast.error("Selecione uma Lista de contatos Meta.");
        return;
      }

      if (!selectedOfficialContactList?.ready) {
        toast.error("Revise os contatos bloqueados ou inválidos antes de criar a campanha.");
        return;
      }

      if (templateVariables.length > 6) {
        toast.error("Este template possui mais de 6 variáveis. A Lista Meta atual permite preencher até 6 campos personalizados.");
        return;
      }

      const normalizedTemplateValues = normalizeTemplateValues(form.templateValues, templateVariables);

      const emptyFixedValue = Object.entries(normalizedTemplateValues).find(([, config]) =>
        config?.source === "fixed" && !String(config.value || "").trim()
      );
      if (emptyFixedValue) {
        toast.error(`Preencha o conteúdo da variável ${emptyFixedValue[0]}.`);
        return;
      }

      if (form.headerMediaUrl && !form.headerMediaUrl.trim().startsWith("https://")) {
        toast.error("A imagem personalizada precisa usar uma URL pública HTTPS.");
        return;
      }

      const templateComponents = JSON.parse(form.templateComponentsJson || "[]");
      const shouldSchedule = mode === "schedule";
      const shouldDispatchNow = mode === "now";
      const scheduledAt = shouldSchedule ? toSchedulePayloadValue(form.scheduledAt) : null;

      if (shouldSchedule && !scheduledAt) {
        toast.error("Informe a data e o horário de agendamento.");
        return;
      }

      const payload = {
        name: form.name || selectedTemplate.name,
        contactListId: null,
        contactListProId: null,
        officialContactListId: form.officialContactListId,
        tagIds: [],
        whatsappId: form.whatsappId,
        templateName: selectedTemplate.name,
        templateLanguage: selectedTemplate.language,
        templateCategory: selectedTemplate.category,
        templateComponents,
        templateValues: {
          ...normalizedTemplateValues,
          ...(form.headerMediaUrl ? { headerMediaUrl: form.headerMediaUrl.trim() } : {})
        },
        safeMode: true,
        requireOptIn: false,
        dailyLimit: Number(form.dailyLimit) || 200,
        hourlyLimit: Number(form.hourlyLimit) || 50,
        stopOnErrorRate: (Number(form.stopOnErrorRate) || 20) / 100,
        stopOnOptOutRate: (Number(form.stopOnOptOutRate) || 5) / 100,
        ticketRegister: form.ticketRegister,
        ticketStatus: form.ticketStatus || "closed",
        ticketQueueId: form.ticketQueueId || null,
        ticketUserId: form.ticketUserId || null,
        executionMode: shouldSchedule ? "scheduled" : "immediate",
        scheduledAt: shouldSchedule ? scheduledAt : null
      };
      if (form.sendInteractive && form.interactiveBodyText) {
        if (form.interactiveType === "button") {
          const buttons = (form.interactiveButtons || []).filter(b => b && b.title && b.title.trim()).slice(0, 3);
          if (buttons.length) {
            payload.interactivePayload = {
              type: "button",
              bodyText: form.interactiveBodyText,
              buttons: buttons.map((b, i) => ({ id: b.id || String(i + 1), title: String(b.title).substring(0, 20) }))
            };
          }
        } else {
          const rows = (form.interactiveSections?.[0]?.rows || []).filter(r => r && r.title && r.title.trim()).slice(0, 10);
          if (rows.length) {
            payload.interactivePayload = {
              type: "list",
              bodyText: form.interactiveBodyText,
              buttonText: form.interactiveButtonText || "Escolha uma opção",
              sections: [{ title: form.interactiveSections?.[0]?.title || "Opções", rows: rows.map((r, i) => ({ id: r.id || String(i + 1), title: String(r.title).substring(0, 24) })) }]
            };
          }
        }
      }
      if (String(form.templateMoreInfoText || "").trim()) {
        payload.interactivePayload = {
          ...(payload.interactivePayload || {}),
          templateButtonActions: {
            moreInfoText: String(form.templateMoreInfoText).trim()
          }
        };
      }

      setLoading(true);
      const { data } = isEditMode
        ? await officialCampaignService.update(campaignId, payload)
        : await officialCampaignService.create(payload);
      const targetCampaignId = data.id;

      if (shouldSchedule && scheduledAt) {
        await officialCampaignService.schedule(targetCampaignId, {
          scheduledAt
        });
        toast.success(isEditMode ? "Rascunho atualizado e campanha agendada com sucesso!" : "Campanha oficial agendada com sucesso!");
      } else if (shouldDispatchNow) {
        await officialCampaignService.schedule(targetCampaignId);
        toast.success(isEditMode ? "Rascunho atualizado e campanha enviada para disparo imediato." : "Campanha oficial enviada para disparo imediato.");
      } else {
        toast.success(isEditMode ? "Rascunho atualizado com sucesso." : "Campanha oficial salva como rascunho.");
      }

      resetForm();
      onSuccess?.();
      onClose();
      history.push(`/official-campaigns/${targetCampaignId}/report`);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const previewWorkspace = (
    <>
      <Paper elevation={0} className={classes.actionPanel}>
        <Typography className={classes.sectionTitle}>Comandos da campanha</Typography>
        <Typography variant="body2" className={classes.previewHint}>
          Salve como rascunho para revisar depois, dispare agora quando a operação estiver pronta ou deixe o horário pré-agendado para a fila assumir.
        </Typography>
        <Box mt={2}>
          <TextField
            fullWidth
            type="datetime-local"
            label="Horário do agendamento"
            InputLabelProps={{ shrink: true }}
            value={form.scheduledAt}
            onChange={handleChange("scheduledAt")}
            helperText="Esse campo só é usado quando você clicar em Agendar."
            inputProps={{ min: getDefaultScheduledAt(1) }}
          />
          <Box className={classes.scheduleQuickActions}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setForm(prev => ({ ...prev, scheduledAt: getDefaultScheduledAt(5) }))}
            >
              +5 min
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setForm(prev => ({ ...prev, scheduledAt: getDefaultScheduledAt(15) }))}
            >
              +15 min
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setForm(prev => ({ ...prev, scheduledAt: getDefaultScheduledAt(60) }))}
            >
              +1 hora
            </Button>
          </Box>
        </Box>
        <Box mt={2} className={classes.actionGrid}>
          <Button
            variant="outlined"
            color="default"
            className={classes.actionButton}
            disabled={loading}
            onClick={() => handleSubmit("draft")}
          >
            {loading ? "Processando..." : "Salvar rascunho"}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            className={classes.actionButton}
            disabled={loading}
            onClick={() => handleSubmit("now")}
          >
            {loading ? "Processando..." : "Disparar agora"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={`${classes.actionButton} ${classes.actionButtonWide}`}
            disabled={loading}
            onClick={() => handleSubmit("schedule")}
          >
            {loading ? "Processando..." : "Agendar campanha"}
          </Button>
        </Box>
      </Paper>

      <Paper elevation={0} className={classes.metricsCard}>
        <Typography className={classes.sectionTitle}>Panorama operacional</Typography>
        <Typography variant="body2" className={classes.previewHint}>
          Estes números ajudam a decidir se a campanha está pronta para sair agora ou se vale deixar em rascunho para revisão.
        </Typography>
        <Box className={classes.metricsGrid}>
          <Box className={classes.metricItem}>
            <Typography className={classes.metricLabel}>Contatos estimados</Typography>
            <Typography className={classes.metricValue}>
              {estimateContacts !== null ? `${estimateContacts}` : "—"}
            </Typography>
          </Box>
          <Box className={classes.metricItem}>
            <Typography className={classes.metricLabel}>Tempo estimado</Typography>
            <Typography className={classes.metricValue}>{estimatedDuration}</Typography>
          </Box>
          <Box className={classes.metricItem}>
            <Typography className={classes.metricLabel}>Conexão</Typography>
            <Typography className={classes.metricValue}>
              {whatsapps.find(w => w.id === form.whatsappId)?.name || "—"}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Paper elevation={0} className={classes.previewCard}>
        <Typography className={classes.sectionTitle}>Prévia operacional do disparo</Typography>
        <Typography variant="body2" className={classes.previewHint}>
          Esta visualização usa o template aprovado, o mapeamento atual das variáveis e os dados de exemplo informados abaixo.
        </Typography>

        <Box mt={2} className={classes.phoneContainer}>
          <Box className={classes.phoneFrame}>
            <Box className={classes.phoneNotch} />
            <Box className={classes.phoneScreen}>
                <Box className={classes.phoneHeader}>
                  <Box className={classes.phoneAvatar}>
                  {getFirstName(previewContact.name).charAt(0) || "A"}
                  </Box>
                  <Box className={classes.phoneHeaderInfo}>
                    <Typography className={classes.phoneHeaderName}>
                    {previewContact.name || "Aluno da Lista Meta"}
                    </Typography>
                    <Typography className={classes.phoneHeaderStatus}>
                    online
                  </Typography>
                </Box>
              </Box>

              <Box className={classes.phoneChat}>
                {(templateHeaderTextPreview || templateBodyPreview || templateFooterPreview || templateHasImage) ? (
                  <>
                    <Box className={classes.phoneBubble}>
                      {templateHasImage && (
                        templateHeaderImageUrl ? (
                          <Box className={classes.previewImageFrame}>
                            <img
                              src={templateHeaderImageUrl}
                              alt="Imagem do template"
                              className={classes.previewImage}
                            />
                          </Box>
                        ) : (
                          <Box className={classes.previewImageFrame}>
                            <Box className={classes.previewImagePlaceholder}>
                              <ImageIcon />
                              <Typography variant="caption">
                                Este template usa imagem no cabeçalho.
                              </Typography>
                              <Typography variant="caption">
                                Selecione a arte para ver a prévia final.
                              </Typography>
                            </Box>
                          </Box>
                        )
                      )}

                      {templateHeaderTextPreview && (
                        <Typography className={classes.phoneMessageHeader}>
                          {templateHeaderTextPreview}
                        </Typography>
                      )}

                      <Typography className={classes.phoneMessageText}>
                        {templateBodyPreview || "O template selecionado não possui corpo de texto."}
                      </Typography>

                      {templateFooterPreview && (
                        <Typography className={classes.phoneMessageFooter}>
                          {templateFooterPreview}
                        </Typography>
                      )}

                      {templateButtonsPreview.length > 0 && (
                        <Box className={classes.templateButtonsWrap}>
                          <Typography className={classes.templateButtonsLabel}>
                            Botões do template
                          </Typography>
                          <Box className={classes.templateButtonsList}>
                            {templateButtonsPreview.map((button) => {
                              const isBlockAction = /bloquear/i.test(button.title);
                              return (
                                <Chip
                                  key={button.id}
                                  size="small"
                                  label={button.title}
                                  className={
                                    isBlockAction
                                      ? classes.templateButtonDangerChip
                                      : classes.templateButtonChip
                                  }
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                      <Box className={classes.phoneMessageTime}>
                        <Typography className={classes.phoneMessageTimeText}>
                          {previewTime}
                        </Typography>
                        <DoneAllIcon className={classes.phoneMessageCheck} />
                      </Box>
                    </Box>

                    {form.sendInteractive && form.interactiveBodyText && (
                      <Box className={`${classes.phoneBubble} ${classes.phoneBubbleSecondary}`}>
                        <Typography className={classes.phoneMessageText}>
                          {form.interactiveBodyText}
                        </Typography>

                        {form.interactiveType === "button" && (
                          <Box className={classes.previewActionWrap}>
                            {(form.interactiveButtons || [])
                              .filter(button => button?.title && button.title.trim())
                              .slice(0, 3)
                              .map(button => (
                                <Chip
                                  key={button.id}
                                  size="small"
                                  label={button.title}
                                  className={classes.previewActionChip}
                                />
                              ))}
                          </Box>
                        )}

                        {form.interactiveType === "list" && (
                          <Box className={classes.previewListCard}>
                            <Typography className={classes.phoneMessageFooter} style={{ marginTop: 0 }}>
                              {form.interactiveSections?.[0]?.title || "Opções"}
                            </Typography>
                            {(form.interactiveSections?.[0]?.rows || [])
                              .filter(row => row?.title && row.title.trim())
                              .slice(0, 10)
                              .map((row, index) => (
                                <Typography key={`${row.id || index}`} className={classes.phoneMessageText}>
                                  {`${index + 1}. ${row.title}`}
                                </Typography>
                              ))}
                            <Box className={classes.previewListButton}>
                              {form.interactiveButtonText || "Escolha uma opção"}
                            </Box>
                          </Box>
                        )}

                        <Box className={classes.phoneMessageTime}>
                          <Typography className={classes.phoneMessageTimeText}>
                            {previewTime}
                          </Typography>
                          <DoneAllIcon className={classes.phoneMessageCheck} />
                        </Box>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box className={classes.emptyPreviewState}>
                    Selecione o template e configure as variáveis para ver a mensagem final aqui.
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>

      <Paper elevation={0} className={classes.previewDataCard}>
        <Typography className={classes.sectionTitle}>Dados de exemplo para a prévia</Typography>
        <Typography variant="body2" className={classes.previewHint}>
          Estes campos servem só para simular o disparo final. Eles não alteram a Lista Meta nem os contatos reais.
        </Typography>

        <Box mt={2}>
          <Typography variant="subtitle2" className={classes.previewFieldLabel}>
            Contato de exemplo
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome completo do destinatário"
                value={previewContact.name}
                onChange={(event) => {
                  const value = event?.target?.value || "";
                  setPreviewContact(prev => ({ ...prev, name: value }));
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Número do destinatário"
                value={previewContact.number}
                onChange={(event) => {
                  const value = event?.target?.value || "";
                  setPreviewContact(prev => ({ ...prev, number: value }));
                }}
                helperText="Usado apenas quando o template aproveita {{number}}."
              />
            </Grid>
          </Grid>
        </Box>

        <Box mt={2.5}>
          <Typography variant="subtitle2" className={classes.previewFieldLabel}>
            Campos simulados da Lista Meta
          </Typography>
          <Grid container spacing={2}>
            {[1, 2, 3, 4, 5, 6].map(number => {
              const key = `var${number}`;
              return (
                <Grid item xs={12} sm={6} key={key}>
                  <TextField
                    fullWidth
                    label={getVariableLabel(selectedOfficialContactList, key)}
                    value={previewContact[key] || ""}
                    onChange={(event) => {
                      const value = event?.target?.value || "";
                      setPreviewContact(prev => ({ ...prev, [key]: value }));
                    }}
                    helperText={`Campo interno: ${key}`}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Box className={classes.previewSummary}>
          <Typography variant="subtitle2" className={classes.previewFieldLabel}>
            Como a campanha está montada agora
          </Typography>
          <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
            {templateVariables.map((variable, index) => {
              const key = `var${index + 1}`;
              const config = getTemplateValueConfig(form.templateValues, key, index);
              const sourceLabel = config.source === "firstName"
                ? "primeiro nome"
                : config.source === "name"
                ? "nome completo"
                : config.source === "listVar"
                ? "Lista Meta"
                : "texto da campanha";
              const sourceSuffix = config.source === "listVar"
                ? ` • ${getVariableLabel(selectedOfficialContactList, config.column || key)}`
                : config.source === "fixed"
                ? ` • ${config.value ? "texto definido" : "texto pendente"}`
                : "";

              return (
                <Chip
                  key={key}
                  size="small"
                  variant="outlined"
                  label={`{{${variable}}} → ${sourceLabel}${sourceSuffix}`}
                />
              );
            })}
          </Box>
        </Box>
      </Paper>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      fullWidth
      maxWidth={false}
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box className={classes.dialogTitleRow}>
          <Box>
            <Typography className={classes.dialogEyebrow}>
              Workspace de campanha oficial
            </Typography>
            <Typography variant="h4">
              {isEditMode ? "Editar campanha oficial" : "Nova campanha oficial"}
            </Typography>
            <Typography variant="body2" className={classes.dialogSubtitle}>
              {isEditMode
                ? "Revise o rascunho salvo, ajuste variáveis e escolha se ele deve continuar em rascunho, sair agora ou entrar na fila em um horário específico."
                : "Monte a campanha, revise as variáveis ao lado do preview e decida se ela deve ficar em rascunho, sair agora ou entrar na fila em um horário específico."}
            </Typography>
          </Box>
          <Box className={classes.helperRow}>
            <Chip size="small" color="primary" variant="outlined" label={`${templateVariables.length} variáveis`} />
            <Chip size="small" variant="outlined" label={`${estimateContacts !== null ? estimateContacts : "—"} contatos`} />
            <Chip size="small" variant="outlined" label={form.scheduledAt ? `Agendamento pronto` : "Sem agendamento"} />
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        {loadingRecord ? (
          <Box py={8} display="flex" flexDirection="column" alignItems="center" justifyContent="center" style={{ gap: 16 }}>
            <CircularProgress />
            <Typography variant="body2" color="textSecondary">
              Carregando o rascunho da campanha...
            </Typography>
          </Box>
        ) : (
        <Grid container spacing={3} className={classes.workspaceLayout}>
          <Grid item xs={12} lg={7}>
            <Paper elevation={0} className={classes.editorPane}>
              <Box className={classes.editorPaneHeader}>
                <Box>
                  <Typography className={classes.editorPaneTitle}>Editor da campanha</Typography>
                  <Typography variant="body2" className={classes.editorPaneSubtitle}>
                    Estruture o público, o template e as regras operacionais antes de disparar.
                  </Typography>
                </Box>
                <Chip size="small" color="primary" variant="outlined" label={`${templateVariables.length} variáveis`} />
              </Box>
              <Box className={classes.editorStack}>
                <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>Público-alvo & Conexão</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Nome da campanha"
              fullWidth
              value={form.name}
              onChange={handleChange("name")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              value={selectedContactListOption}
              onChange={(_, option) => {
                if (!option) {
                  setForm((prev) => ({ ...prev, officialContactListId: "" }));
                  return;
                }
                setForm((prev) => ({ ...prev, officialContactListId: option.id }));
              }}
              options={contactListOptions}
              getOptionLabel={(option) => (option && option.label) || ""}
              filterOptions={createFilterOptions({ trim: true })}
              fullWidth
              size="small"
              noOptionsText="Nenhuma lista encontrada"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Lista de contatos Meta"
                  variant="outlined"
                  placeholder="Pesquisar lista..."
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="whatsapp-label">Conexão oficial</InputLabel>
              <Select
                labelId="whatsapp-label"
                value={form.whatsappId}
                onChange={(event) => {
                  const value = event?.target?.value || "";
                  setForm(prev => ({ ...prev, whatsappId: value }));
                  handleLoadTemplates(value);
                }}
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {whatsapps.filter(w => w?.id).map(w => (
                  <MenuItem key={w.id} value={w.id}>
                    {asText(w.name || w.phone_number, "Conexao oficial")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {form.whatsappId && (
              <Box mt={1}>
                <span
                  className={classes.offlineDot}
                  style={{
                    backgroundColor: connectionStatus === "CONNECTED" ? "#4caf50" : "#f44336"
                  }}
                />
                <Typography variant="body2" component="span">
                  {connectionStatus === "CONNECTED" ? "Online" : "Offline"}
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>Configurações de Tickets</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.ticketRegister}
                  onChange={handleChange("ticketRegister")}
                  color="primary"
                />
              }
              label="Registrar no sistema"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="ticket-status-label">Status do ticket</InputLabel>
              <Select
                labelId="ticket-status-label"
                value={form.ticketStatus}
                onChange={handleChange("ticketStatus")}
              >
                <MenuItem value="closed">Encerrado</MenuItem>
                <MenuItem value="pending">Pendente</MenuItem>
                <MenuItem value="open">Aberto</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="queue-label">Fila</InputLabel>
              <Select
                labelId="queue-label"
                value={form.ticketQueueId}
                onChange={handleChange("ticketQueueId")}
              >
                <MenuItem value="">
                  <em>Sem fila</em>
                </MenuItem>
                {queues.filter(queue => queue?.id).map(queue => (
                  <MenuItem key={queue.id} value={queue.id}>
                    {asText(queue.name, "Fila sem nome")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="user-label">Usuário responsável</InputLabel>
              <Select
                labelId="user-label"
                value={form.ticketUserId}
                onChange={handleChange("ticketUserId")}
              >
                <MenuItem value="">
                  <em>Automático</em>
                </MenuItem>
                {users.filter(u => u?.id).map(u => (
                  <MenuItem key={u.id} value={u.id}>
                    {asText(u.name, "Usuario sem nome")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>Segurança do envio</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Limite por hora"
              type="number"
              fullWidth
              value={form.hourlyLimit}
              onChange={handleChange("hourlyLimit")}
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Limite por dia"
              type="number"
              fullWidth
              value={form.dailyLimit}
              onChange={handleChange("dailyLimit")}
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Erro máximo (%)"
              type="number"
              fullWidth
              value={form.stopOnErrorRate}
              onChange={handleChange("stopOnErrorRate")}
              inputProps={{ min: 1, max: 100 }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Opt-out máximo (%)"
              type="number"
              fullWidth
              value={form.stopOnOptOutRate}
              onChange={handleChange("stopOnOptOutRate")}
              inputProps={{ min: 1, max: 100 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>Template da campanha</Typography>
          </Grid>
          {!selectedContactListOption && (
            <Grid item xs={12}>
              <Paper variant="outlined" className={classes.sectionCard} style={{ background: "#fff8e1" }}>
                <Typography variant="body2" color="textSecondary">
                  Selecione uma lista de contatos para liberar o template e o preenchimento das variáveis.
                </Typography>
              </Paper>
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="template-label">Selecione o template</InputLabel>
              <Select
                labelId="template-label"
                value={selectedTemplate ? getTemplateKey(selectedTemplate) : ""}
                onChange={(event) => {
                  const templateKey = event?.target?.value || "";
                  const template = templates.find(t => getTemplateKey(t) === templateKey);
                  if (template) {
                    handleSelectTemplate(template);
                  }
                }}
                disabled={!selectedContactListOption || !templates.length || loadingTemplates}
              >
                <MenuItem value="">
                  <em>{loadingTemplates ? "Carregando..." : "Selecione"}</em>
                </MenuItem>
                {templates.filter(template => getTemplateKey(template)).map(template => (
                  <MenuItem key={getTemplateKey(template)} value={getTemplateKey(template)}>
                    {asText(template.name, "Modelo sem nome")} ({asText(template.language, "sem idioma")})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="caption" color="textSecondary">
                Atualize a lista depois que a Meta aprovar novos modelos. Somente modelos aprovados ficam disponíveis.
              </Typography>
              <Button
                size="small"
                color="primary"
                variant="outlined"
                onClick={handleSyncTemplates}
                disabled={!form.whatsappId || syncingTemplates}
              >
                {syncingTemplates ? "Sincronizando..." : "Sincronizar modelos da Meta"}
              </Button>
            </Box>
          </Grid>
          {selectedTemplate && (
            <>
              <Grid item xs={12}>
                <Paper variant="outlined" className={classes.sectionCard} style={{ background: "#fafafa" }}>
                  <Typography className={classes.sectionTitle}>Variáveis do template</Typography>
                  <Typography variant="body2" className={classes.sectionHint}>
                    A Meta aprova a estrutura. Aqui você liga cada posição aos dados da Lista Meta selecionada.
                  </Typography>
                  <Box mt={1.5} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                    {templateVariables.map((variable, index) => {
                      const key = `var${index + 1}`;
                      const config = getTemplateValueConfig(form.templateValues, key, index);
                      const sourceLabel = config.source === "firstName"
                        ? "primeiro nome"
                        : config.source === "name"
                        ? "nome completo"
                        : config.source === "listVar"
                        ? "Lista Meta"
                        : "texto da campanha";
                      const sourceSuffix = config.source === "listVar"
                        ? ` • ${config.column || key}`
                        : config.source === "fixed"
                        ? ` • ${config.value ? "texto fixo" : "pendente"}`
                        : "";
                      return (
                        <Chip
                          key={key}
                          size="small"
                          variant="outlined"
                          label={`{{${variable}}} → ${sourceLabel}${sourceSuffix}`}
                        />
                      );
                    })}
                  </Box>
                </Paper>
              </Grid>
              {templateVariables.map((variable, index) => {
                const key = `var${index + 1}`;
                const config = getTemplateValueConfig(form.templateValues, key, index);
                return (
                  <React.Fragment key={key}>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>{`{{${variable}}} - origem do conteúdo`}</InputLabel>
                        <Select
                          value={config.source || "fixed"}
                          onChange={(event) => {
                            const source = event?.target?.value || "fixed";
                            setForm(prev => ({
                              ...prev,
                              templateValues: {
                                ...prev.templateValues,
                                [key]: {
                                  ...getTemplateValueConfig(prev.templateValues, key, index),
                                  source
                                }
                              }
                            }));
                          }}
                        >
                          <MenuItem value="firstName">Primeiro nome do aluno</MenuItem>
                          <MenuItem value="name">Nome completo do aluno</MenuItem>
                          <MenuItem value="fixed">Texto definido nesta campanha</MenuItem>
                          <MenuItem value="listVar">Campo da Lista Meta</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      {config.source === "listVar" ? (
                        <FormControl fullWidth>
                          <InputLabel>
                            Campo da Lista Meta
                          </InputLabel>
                          <Select
                            value={config.column || key}
                            onChange={(event) => {
                              const column = event?.target?.value || key;
                              setForm(prev => ({
                                ...prev,
                                templateValues: {
                                  ...prev.templateValues,
                                  [key]: {
                                    ...getTemplateValueConfig(prev.templateValues, key, index),
                                    column
                                  }
                                }
                              }));
                            }}
                          >
                            {[1, 2, 3, 4, 5, 6].map(number => (
                              <MenuItem key={number} value={`var${number}`}>
                                {selectedOfficialContactList?.variableLabels?.[`var${number}`] || `Variável ${number}`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          fullWidth
                          disabled={config.source === "firstName" || config.source === "name"}
                          label={`Conteúdo de {{${variable}}}`}
                          value={config.value || ""}
                          onChange={(event) => {
                            const value = event?.target?.value || "";
                            const source = config.source || "fixed";
                            setForm(prev => ({
                              ...prev,
                              templateValues: {
                                ...prev.templateValues,
                                [key]: {
                                  ...getTemplateValueConfig(prev.templateValues, key, index),
                                  source,
                                  value
                                }
                              }
                            }));
                          }}
                          helperText={
                            index === 0
                              ? "Normalmente este campo recebe automaticamente o nome do aluno."
                              : "Use uma informação acadêmica objetiva. Não inclua ofertas ou divulgação comercial."
                          }
                        />
                      )}
                    </Grid>
                  </React.Fragment>
                );
              })}
              {templateHasMoreInfoButton && (
                <Grid item xs={12}>
                  <Paper variant="outlined" className={classes.sectionCard} style={{ background: "#fbfbfb" }}>
                    <Typography className={classes.sectionTitle}>Resposta automática do botão "Mais informações"</Typography>
                    <Typography variant="body2" className={classes.sectionHint}>
                      Esse texto vai ser enviado quando o lead tocar nesse botão do template. Você pode usar texto normal e links do WhatsApp ou do site.
                    </Typography>
                    <Box mt={1.5}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        label="Mensagem automática"
                        value={form.templateMoreInfoText}
                        onChange={handleChange("templateMoreInfoText")}
                        placeholder="Ex: Perfeito, Joel. Aqui estão os detalhes do curso..."
                        helperText="Se ficar em branco, o clique será apenas registrado sem resposta automática."
                      />
                    </Box>
                  </Paper>
                </Grid>
              )}
              {templateHasImage && (
                <Grid item xs={12}>
                  <Box p={2} border={1} borderColor="divider" borderRadius={8}>
                    <Typography className={classes.sectionTitle}>
                      Imagem do comunicado
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Selecione uma arte do computador. O sistema envia a imagem e gera a URL HTTPS exigida pela Meta.
                    </Typography>

                    <Box mt={2} display="flex" alignItems="center" style={{ gap: 12 }}>
                      <input
                        accept="image/png,image/jpeg,image/jpg"
                        id="official-campaign-header-image"
                        type="file"
                        style={{ display: "none" }}
                        onChange={handleHeaderImageUpload}
                      />
                      <label htmlFor="official-campaign-header-image">
                        <Button
                          component="span"
                          variant="outlined"
                          color="primary"
                          disabled={uploadingHeaderImage}
                        >
                          {uploadingHeaderImage ? "Enviando imagem..." : "Selecionar imagem do computador"}
                        </Button>
                      </label>
                      {uploadingHeaderImage && <CircularProgress size={22} />}
                    </Box>

                    <Box mt={2}>
                      <TextField
                        fullWidth
                        label="URL HTTPS da imagem"
                        value={form.headerMediaUrl}
                        onChange={handleChange("headerMediaUrl")}
                        helperText="A URL é preenchida automaticamente após selecionar a imagem. Você ainda pode colar uma URL HTTPS manualmente."
                      />
                    </Box>

                    {form.headerMediaUrl && (
                      <Box mt={2}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Prévia da imagem selecionada:
                        </Typography>
                        <Box
                          mt={1}
                          border={1}
                          borderColor="divider"
                          borderRadius={8}
                          style={{ overflow: "hidden", maxWidth: 360 }}
                        >
                          <img
                            src={form.headerMediaUrl}
                            alt="Imagem do comunicado"
                            style={{ display: "block", width: "100%", height: "auto" }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}
            </>
          )}
          <Grid item xs={12} style={{ display: "none" }}>
            <TextField
              label="Estrutura aprovada pela Meta"
              multiline
              fullWidth
              minRows={6}
              variant="outlined"
              value={form.templateComponentsJson}
              InputProps={{ readOnly: true }}
              className={classes.templateEditor}
            />
            <Typography variant="caption" color="textSecondary" display="block">
              A estrutura aprovada não pode ser alterada. As variáveis posicionais da Meta, como {"{{1}}"}
              e {"{{2}}"}, podem ser ligadas aos campos personalizados da Lista Meta.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>Mensagem com botões/lista (opcional)</Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.sendInteractive || false}
                  onChange={(event) => {
                    const checked = Boolean(event?.target?.checked);
                    setForm(prev => ({ ...prev, sendInteractive: checked }));
                  }}
                  color="primary"
                />
              }
              label="Enviar também mensagem com botões ou lista após o template"
            />
          </Grid>
          {form.sendInteractive && (
            <>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={form.interactiveType}
                    onChange={(event) => {
                      const value = event?.target?.value || "button";
                      setForm(prev => ({ ...prev, interactiveType: value }));
                    }}
                    label="Tipo"
                  >
                    <MenuItem value="button">Botões (até 3)</MenuItem>
                    <MenuItem value="list">Lista</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Texto da mensagem"
                  fullWidth
                  size="small"
                  value={form.interactiveBodyText}
                  onChange={(event) => {
                    const value = event?.target?.value || "";
                    setForm(prev => ({ ...prev, interactiveBodyText: value }));
                  }}
                  placeholder="Ex: Escolha uma opção abaixo"
                />
              </Grid>
              {form.interactiveType === "button" && (
                [1, 2, 3].map((i) => (
                  <Grid item xs={12} md={4} key={i}>
                    <TextField
                      label={`Botão ${i}`}
                      fullWidth
                      size="small"
                      inputProps={{ maxLength: 20 }}
                      value={form.interactiveButtons?.[i - 1]?.title || ""}
                      onChange={(event) => {
                        const value = event?.target?.value || "";
                        const next = [...(form.interactiveButtons || defaultForm.interactiveButtons)];
                        next[i - 1] = { ...next[i - 1], id: String(i), title: value };
                        setForm(prev => ({ ...prev, interactiveButtons: next }));
                      }}
                    />
                  </Grid>
                ))
              )}
              {form.interactiveType === "list" && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Texto do botão que abre a lista"
                      fullWidth
                      size="small"
                      value={form.interactiveButtonText}
                      onChange={(event) => {
                        const value = event?.target?.value || "";
                        setForm(prev => ({ ...prev, interactiveButtonText: value }));
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Itens da lista (um por linha ou use até 10 itens)</Typography>
                    {(form.interactiveSections?.[0]?.rows || [{ id: "1", title: "" }]).map((row, idx) => (
                      <Box key={idx} display="flex" alignItems="center" gap={1} mt={0.5}>
                        <TextField
                          label={`Item ${idx + 1}`}
                          size="small"
                          fullWidth
                          inputProps={{ maxLength: 24 }}
                          value={row.title || ""}
                          onChange={(event) => {
                            const value = event?.target?.value || "";
                            const sec = form.interactiveSections?.[0] || { title: "Opções", rows: [] };
                            const rows = [...(sec.rows || [])];
                            while (rows.length <= idx) rows.push({ id: String(rows.length + 1), title: "" });
                            rows[idx] = { ...rows[idx], id: rows[idx].id || String(idx + 1), title: value };
                            setForm(prev => ({ ...prev, interactiveSections: [{ ...sec, rows }] }));
                          }}
                        />
                        {(form.interactiveSections?.[0]?.rows || []).length > 1 && (
                          <Button
                            size="small"
                            color="secondary"
                            onClick={() => {
                              const sec = form.interactiveSections?.[0] || { title: "Opções", rows: [] };
                              const rows = (sec.rows || []).filter((_, j) => j !== idx);
                              setForm(prev => ({ ...prev, interactiveSections: [{ ...sec, rows: rows.length ? rows : [{ id: "1", title: "" }] }] }));
                            }}
                          >
                            Remover
                          </Button>
                        )}
                      </Box>
                    ))}
                    <Button
                      size="small"
                      onClick={() => {
                        const sec = form.interactiveSections?.[0] || { title: "Opções", rows: [] };
                        const rows = [...(sec.rows || []), { id: String((sec.rows || []).length + 1), title: "" }];
                        if (rows.length <= 10) setForm(prev => ({ ...prev, interactiveSections: [{ ...sec, rows }] }));
                      }}
                      disabled={(form.interactiveSections?.[0]?.rows || []).length >= 10}
                      style={{ marginTop: 8 }}
                    >
                      + Adicionar item
                    </Button>
                  </Grid>
                </>
              )}
            </>
          )}

                </Grid>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} lg={5}>
            <Box className={classes.previewPane}>
              <Paper elevation={0} className={classes.sectionCard} style={{ background: "#eef5ff" }}>
                <Typography className={classes.sectionTitle}>Prévia operacional</Typography>
                <Typography variant="body2" className={classes.sectionHint}>
                  Ajuste os dados de exemplo e confira a mensagem final antes de salvar, agendar ou disparar agora.
                </Typography>
              </Paper>
              <Box className={classes.stickyColumn}>
                {previewWorkspace}
              </Box>
            </Box>
          </Grid>
        </Grid>
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Box className={classes.footerActionsWrap}>
          <Typography variant="body2" className={classes.footerHint}>
            O rascunho vai para o relatório da campanha para você decidir o próximo passo. O disparo imediato usa a fila oficial agora, e o agendamento respeita a data informada no painel lateral.
          </Typography>
          <Box className={classes.footerButtons}>
            <Button onClick={onClose} color="default">
              Fechar
            </Button>
            <Button
              variant="outlined"
              color="default"
              onClick={() => handleSubmit("draft")}
              disabled={loading}
            >
              {loading ? "Processando..." : "Salvar rascunho"}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleSubmit("now")}
              disabled={loading}
            >
              {loading ? "Processando..." : "Disparar agora"}
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => handleSubmit("schedule")}
              disabled={loading}
            >
              {loading ? "Processando..." : "Agendar"}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default OfficialCampaignModal;
